import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import CountUp from 'react-countup';
import { useTranslation } from 'react-i18next';
import { Shield, MapPin, Zap, Brain, Eye, TrendingUp, Users, Truck, GraduationCap, DollarSign, Activity, ChevronRight, Lock, Globe, Star } from 'lucide-react';

const AnimCounter = ({ end, suffix='', decimals=0, duration=2 }: any) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return <span ref={ref}>{inView ? <CountUp end={end} suffix={suffix} decimals={decimals} duration={duration} preserveValue/> : `0${suffix}`}</span>;
};

const Particle = ({ delay, x, y, size }: any) => (
  <motion.div className="absolute rounded-full pointer-events-none"
    style={{ left:`${x}%`, top:`${y}%`, width:size, height:size, background:'radial-gradient(circle,rgba(234,179,8,.55) 0%,rgba(234,179,8,0) 70%)' }}
    animate={{ y:[0,-35,0], opacity:[0,.65,0], scale:[0,1,0] }}
    transition={{ duration:4+Math.random()*3, delay, repeat:Infinity, ease:'easeInOut' }}/>
);

const MOD_META = [
  { icon:DollarSign,    color:'#10b981', bg:'rgba(16,185,129,.12)',  kpi:'2.3Md F',  kpiLabel:'Détectés via ML',          emoji:'💰' },
  { icon:Users,         color:'#3b82f6', bg:'rgba(59,130,246,.12)',  kpi:'20 000+',  kpiLabel:'Agents de police suivis',   emoji:'👥' },
  { icon:Eye,           color:'#ef4444', bg:'rgba(239,68,68,.12)',   kpi:'< 12 min', kpiLabel:'Délai réponse moyen P1',   emoji:'🚔' },
  { icon:Truck,         color:'#f97316', bg:'rgba(249,115,22,.12)',  kpi:'−34%',kpiLabel:'Anomalies carburant',       emoji:'🚚' },
  { icon:GraduationCap, color:'#a855f7', bg:'rgba(168,85,247,.12)', kpi:'+47%',     kpiLabel:'Uplift performance agents', emoji:'📚' },
  { icon:TrendingUp,    color:'#06b6d4', bg:'rgba(6,182,212,.12)',   kpi:'1.15 Md F',  kpiLabel:'Économies annuelles estimées',       emoji:'⚡' },
];

const STATS = [
  { icon:MapPin,   val:10,   suffix:'',  label:'Régions couvertes',     color:'#3b82f6' },
  { icon:Shield,   val:58,   suffix:'',  label:'Villes connectées',     color:'#10b981' },
  { icon:Users,    val:8241, suffix:'+', label:'Agents sous NEXUS360',       color:'#a855f7' },
  { icon:Zap,      val:6,    suffix:'',  label:'Modules intégrés', color:'#f97316' },
  { icon:Activity, val:99.7, suffix:'%', label:'Disponibilité 24/7',    color:'#eab308', decimals:1 },
  { icon:Eye,      val:30,   suffix:'s', label:'Actualisation données', color:'#06b6d4' },
];

const PILLARS = [
  { icon:Lock,  color:'#eab308', title:'Souveraineté des données',
    desc:"Une Architecture hybride de classe mondiale avec hébergement on-premise DGSN. Aucune donnée sensible ne quitte le territoire.",
    stat:'100%', statLabel:'Données critiques en local' },
  { icon:Brain, color:'#3b82f6', title:'Intelligence prédictive',
    desc:"6 modèles ML déployés : prédiction incidents, fraude carburant, agents fictifs, gaps compétences, TCO, risque sécuritaire. NEXUS360 travaille pendant que vous commandez.",
    stat:'94%', statLabel:'Précision modèles ML' },
  { icon:Globe, color:'#10b981', title:'Décision en temps réel',
    desc:"Actualisation toutes les 30 secondes. Tableau de bord exécutif sur 10 régions, 58 villes, 20 000 agents. Du terrain au bureau du DG en moins d'une minute.",
    stat:'30s', statLabel:"Cycle d'actualisation" },
];

export const Welcome = ({ user }: { user: any }) => {
  const { t } = useTranslation();
  const [activeModule, setActiveModule] = useState(0);
  const [phase, setPhase]               = useState(0);
  const [visible, setVisible]           = useState(true);

  const mData = (t('welcome.modules_showcase.modules', { returnObjects:true }) as any[]) || [];
  const modules = mData.map((m, i) => ({ ...m, ...MOD_META[i] }));

  useEffect(() => {
    const iv = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        setPhase(p => { const next=(p+1)%3; if(next===0) setActiveModule(a=>(a+1)%modules.length); return next; });
        setVisible(true);
      }, 500);
    }, 5500);
    return () => clearInterval(iv);
  }, [modules.length]);

  const mod = modules[activeModule] || MOD_META[0];
  const ModIcon = mod.icon;
  const phases = [
    { label:`\u26a0 ${t('welcome.modules_showcase.phase_challenge')}`, text:mod.challenge, color:mod.color },
    { label:`\ud83d\udcc9 ${t('welcome.modules_showcase.phase_impact')}`,    text:mod.impact,    color:'#f87171' },
    { label:`\ud83c\udfaf ${t('welcome.modules_showcase.phase_vision')}`,    text:mod.vision,    color:'#fbbf24' },
  ];
  const cur = phases[phase];
  const particles = Array.from({ length:18 }, (_,i) => ({ delay:i*.35, x:5+Math.random()*90, y:5+Math.random()*90, size:3+Math.random()*5 }));

  return (
    <div className="min-h-screen" style={{ background:'linear-gradient(160deg,#020617 0%,#0f172a 55%,#020617 100%)' }}>

      {/* HERO */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage:'linear-gradient(rgba(234,179,8,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(234,179,8,.04) 1px,transparent 1px)', backgroundSize:'56px 56px' }}/>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none" style={{ background:'radial-gradient(ellipse,rgba(234,179,8,.07) 0%,transparent 70%)' }}/>
        {particles.map((p,i)=><Particle key={i} {...p}/>)}
        <div className="relative z-10 px-6 md:px-12 pt-12 pb-8 max-w-6xl mx-auto">
          <motion.div initial={{opacity:0,y:-14}} animate={{opacity:1,y:0}} transition={{duration:.55}}
            className="flex items-center justify-center gap-3 mb-5">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-yellow-500/40"/>
            <span className="text-yellow-500/75 text-sm font-bold tracking-[.28em] uppercase">République du Cameroun · DGSN</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-yellow-500/40"/>
          </motion.div>
          <motion.div initial={{opacity:0,y:22}} animate={{opacity:1,y:0}} transition={{duration:.65,delay:.12}} className="text-center mb-3">
            <h1 className="text-5xl md:text-7xl font-black tracking-tight leading-none mb-3">
              <span className="text-white">NEXUS</span>
              <motion.span style={{ background:'linear-gradient(90deg,#eab308 0%,#f97316 50%,#eab308 100%)', backgroundClip:'text', WebkitBackgroundClip:'text', color:'transparent', backgroundSize:'200%' }}
                animate={{backgroundPosition:['0% 50%','100% 50%','0% 50%']}} transition={{duration:4,repeat:Infinity,ease:'linear'}}>360</motion.span>
            </h1>
            <p className="text-lg md:text-xl text-white/85 font-semibold">{t('welcome.subtitle')}</p>
          </motion.div>
          <motion.div initial={{opacity:0,scale:.96}} animate={{opacity:1,scale:1}} transition={{delay:.45}}
            className="max-w-3xl mx-auto mb-8 rounded-xl border border-yellow-500/15 overflow-hidden"
            style={{background:'linear-gradient(135deg,rgba(234,179,8,.06) 0%,rgba(249,115,22,.04) 100%)',backdropFilter:'blur(10px)'}}>
            <div className="flex items-center gap-2 px-5 py-1.5 border-b border-yellow-500/10">
              <motion.div className="w-1.5 h-1.5 rounded-full bg-green-400" animate={{opacity:[1,.2,1]}} transition={{duration:1.1,repeat:Infinity}}/>
              <span className="text-green-400 text-sm font-bold tracking-widest">SYSTÈME ACTIF · DONNÉES EN DIRECT</span>
            </div>
            <div className="px-5 py-4 border-l-4 border-yellow-500/60">
              <p className="text-slate-200 text-base md:text-base leading-relaxed">{t('welcome.message1')}</p>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="px-6 md:px-12 max-w-6xl mx-auto pb-12">

        {/* MODULE CAROUSEL */}
        <motion.div initial={{opacity:0,y:18}} animate={{opacity:1,y:0}} transition={{delay:.6}} className="mb-10">
          <div className="text-center mb-5">
            <p className="text-yellow-500/55 text-sm font-bold tracking-[.28em] uppercase mb-1">6 MODULES · 1 PLATEFORME</p>
            <h2 className="text-xl md:text-2xl font-bold text-white">Chaque module, un levier de souveraineté</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mb-5">
            {modules.map((m:any,i:number)=>{ const Icon=m.icon; return (
              <motion.button key={i} whileHover={{scale:1.05}} whileTap={{scale:.96}}
                onClick={()=>{setActiveModule(i);setPhase(0);setVisible(true);}}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold transition-all border ${activeModule===i?'border-transparent':'text-slate-400 border-slate-700/50 hover:text-white'}`}
                style={activeModule===i?{background:m.bg,borderColor:m.color+'40',color:m.color}:{}}>
                <span>{m.emoji} {m.title?.split(' ').slice(-2).join(' ')}</span>
              </motion.button>
            );})}
          </div>
          <AnimatePresence mode="wait">
            <motion.div key={activeModule} initial={{opacity:0,y:16,scale:.97}} animate={{opacity:1,y:0,scale:1}} exit={{opacity:0,y:-16,scale:.97}} transition={{duration:.3}}
              className="rounded-2xl overflow-hidden border" style={{borderColor:mod.color+'28',background:'linear-gradient(135deg,rgba(2,6,23,.97) 0%,rgba(15,23,42,.92) 100%)'}}>
              <div className="h-1" style={{background:`linear-gradient(90deg,transparent,${mod.color},transparent)`}}/>
              <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2 flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <motion.div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{background:mod.bg,border:`1px solid ${mod.color}35`}}
                      animate={{boxShadow:[`0 0 0px ${mod.color}00`,`0 0 20px ${mod.color}35`,`0 0 0px ${mod.color}00`]}}
                      transition={{duration:2.5,repeat:Infinity}}>
                      <ModIcon className="w-6 h-6" style={{color:mod.color}}/>
                    </motion.div>
                    <div>
                      <p className="text-sm font-bold tracking-widest mb-0.5" style={{color:mod.color}}>MODULE</p>
                      <h3 className="text-white font-bold text-lg leading-tight">{mod.title}</h3>
                    </div>
                  </div>
                  <div className="rounded-xl p-4 border-l-4 min-h-20"
                    style={{borderColor:cur.color,background:'rgba(255,255,255,.03)',opacity:visible?1:0,transform:visible?'translateY(0)':'translateY(10px)',transition:'opacity .4s ease,transform .4s ease'}}>
                    <p className="text-sm font-bold uppercase tracking-widest mb-1.5" style={{color:cur.color}}>{cur.label}</p>
                    <p className="text-slate-200 text-base leading-relaxed">{cur.text}</p>
                  </div>
                  <div className="flex gap-1.5">
                    {phases.map((_,i)=>(
                      <motion.div key={i} className="h-1 flex-1 rounded-full cursor-pointer"
                        style={{background:i===phase?cur.color:'rgba(255,255,255,.1)'}}
                        onClick={()=>{setPhase(i);setVisible(true);}} whileHover={{scaleY:2}}/>
                    ))}
                  </div>
                  <div className="flex gap-1.5">
                    {modules.map((_:any,i:number)=>(
                      <button key={i} onClick={()=>{setActiveModule(i);setPhase(0);}}
                        className="w-2 h-2 rounded-full transition-all"
                        style={{background:i===activeModule?mod.color:'rgba(255,255,255,.2)',transform:i===activeModule?'scale(1.4)':'scale(1)'}}/>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center rounded-xl p-5 border" style={{borderColor:mod.color+'20',background:mod.bg}}>
                  <motion.p className="font-black text-4xl md:text-5xl leading-none mb-2 text-center" style={{color:mod.color}}
                    initial={{scale:0}} animate={{scale:1}} transition={{type:'spring',stiffness:220,delay:.1}}>
                    {mod.kpi}
                  </motion.p>
                  <p className="text-slate-300 text-sm font-medium text-center">{mod.kpiLabel}</p>
                  <div className="mt-3 flex items-center gap-1.5">
                    <motion.div className="w-1.5 h-1.5 rounded-full" style={{background:mod.color}} animate={{opacity:[1,.3,1]}} transition={{duration:1.2,repeat:Infinity}}/>
                    <span className="text-sm text-slate-500">En temps réel</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* ORIGINAL message2 + welcome_text + tagline */}
        <motion.div initial={{opacity:0,y:14}} whileInView={{opacity:1,y:0}} viewport={{once:true}}
          className="rounded-xl border border-yellow-500/15 p-5 md:p-6 mb-8"
          style={{background:'linear-gradient(135deg,rgba(234,179,8,.05) 0%,rgba(15,23,42,.9) 100%)'}}>
          <div className="border-l-4 border-yellow-500/60 pl-4 mb-5">
            <p className="text-slate-200 text-base md:text-base leading-relaxed">{t('welcome.message2')}</p>
          </div>
          <div className="text-center">
            <p className="text-xl md:text-2xl font-black mb-1"
              style={{background:'linear-gradient(90deg,#eab308,#f97316)',backgroundClip:'text',WebkitBackgroundClip:'text',color:'transparent'}}>
              {t('welcome.welcome_text')}
            </p>
            <p className="text-base md:text-lg text-white font-semibold tracking-wide">{t('welcome.tagline')}</p>
          </div>
        </motion.div>


        {/* ANIMATED STATS */}
        <motion.div initial={{opacity:0,y:16}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:.5}} className="mb-10">
          <h2 className="text-xl font-bold text-white mb-5 text-center">{t('welcome.stats_title')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {STATS.map((s,i)=>{ const Icon=s.icon; return (
              <motion.div key={i} whileHover={{scale:1.07,y:-4}} transition={{duration:.16}}
                className="rounded-xl p-4 border border-white/5 text-center relative overflow-hidden cursor-default"
                style={{background:'linear-gradient(135deg,rgba(15,23,42,.92) 0%,rgba(30,41,59,.7) 100%)'}}>
                <motion.div className="absolute inset-0 opacity-0" whileHover={{opacity:1}}
                  style={{background:`radial-gradient(ellipse at center,${s.color}18 0%,transparent 70%)`}}/>
                <Icon className="w-5 h-5 mx-auto mb-2" style={{color:s.color}}/>
                <p className="font-black text-2xl leading-none mb-1" style={{color:s.color}}>
                  <AnimCounter end={s.val} suffix={s.suffix} decimals={(s as any).decimals||0} duration={2+i*.18}/>
                </p>
                <p className="text-slate-400 text-sm leading-tight">{s.label}</p>
              </motion.div>
            );})}
          </div>
        </motion.div>

        {/* THREE PILLARS */}
        <motion.div initial={{opacity:0,y:18}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:.55}} className="mb-10">
          <div className="text-center mb-6">
            <p className="text-yellow-500/55 text-sm font-bold tracking-[.28em] uppercase mb-1">ARCHITECTURE SOUVERAINE</p>
            <h2 className="text-xl md:text-2xl font-bold text-white">Les trois piliers de la souveraineté opérationnelle</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PILLARS.map((p,i)=>{ const PIcon=p.icon; return (
              <motion.div key={i} whileHover={{scale:1.02,boxShadow:`0 0 28px ${p.color}12`}} transition={{duration:.18}}
                className="rounded-2xl p-5 border relative overflow-hidden"
                style={{borderColor:p.color+'22',background:'linear-gradient(135deg,rgba(15,23,42,.95) 0%,rgba(30,41,59,.8) 100%)'}}>
                <div className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
                  style={{background:`radial-gradient(ellipse at top right,${p.color}10 0%,transparent 70%)`}}/>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3"
                  style={{background:`${p.color}14`,border:`1px solid ${p.color}28`}}>
                  <PIcon className="w-5 h-5" style={{color:p.color}}/>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{p.title}</h3>
                <p className="text-slate-400 text-base leading-relaxed mb-4">{p.desc}</p>
                <div className="flex items-baseline gap-2 pt-3 border-t" style={{borderColor:p.color+'14'}}>
                  <span className="font-black text-2xl" style={{color:p.color}}>{p.stat}</span>
                  <span className="text-slate-400 text-sm">{p.statLabel}</span>
                </div>
              </motion.div>
            );})}
          </div>
        </motion.div>

        {/* ORIGINAL footer */}
        <motion.div initial={{opacity:0,y:14}} whileInView={{opacity:1,y:0}} viewport={{once:true}}
          className="rounded-2xl p-6 md:p-8 border border-yellow-500/15 text-center relative overflow-hidden mb-8"
          style={{background:'linear-gradient(135deg,rgba(234,179,8,.05) 0%,rgba(249,115,22,.04) 50%,rgba(234,179,8,.05) 100%)'}}>
          <div className="absolute inset-0 pointer-events-none" style={{backgroundImage:'radial-gradient(ellipse at 50% 50%,rgba(234,179,8,.07) 0%,transparent 65%)'}}/>
          <motion.div animate={{rotate:360}} transition={{duration:35,repeat:Infinity,ease:'linear'}}
            className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-yellow-500/5 pointer-events-none"/>
          <div className="relative z-10">
            <Star className="w-6 h-6 text-yellow-500/60 mx-auto mb-3"/>
            <p className="text-white font-black text-xl md:text-2xl mb-4"
              style={{background:'linear-gradient(90deg,#eab308,#f97316,#eab308)',backgroundClip:'text',WebkitBackgroundClip:'text',color:'transparent'}}>
              {t('welcome.footer')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {['Données souveraines','IA embarquée','Temps réel','Multi-rôles','Auditabilité totale'].map((tag,i)=>(
                <motion.span key={i} whileHover={{scale:1.07}}
                  className="flex items-center gap-1 px-3 py-1 rounded-full border border-yellow-500/20 text-yellow-400/75 bg-yellow-500/5 text-sm font-medium">
                  <ChevronRight className="w-3 h-3"/>{tag}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="text-center pt-4 border-t border-white/5">
          <p className="text-slate-700 text-sm">
            © 2026 Délégation Générale à la Sûreté Nationale du Cameroun ·
            Powered by <span className="text-yellow-500/45 font-medium">Analytix Engineering</span>
          </p>
        </div>
      </div>
    </div>
  );
};
